import { isEmpty, isNil } from "lodash";
import { useActivityPageContext } from "PFApp/activities/show/activity_page_context";
import { useActivityMatchesFilters } from "PFApp/activities/show/hooks/use_activity_matches_filters";
import { useActivityTemplate } from "PFApp/activities/show/hooks/use_activity_template";
import { useMatchesStatusText } from "PFApp/activities/show/hooks/use_matches_status_text";
import { MatchesEmpty } from "PFApp/activities/show/matches/matches_empty";
import { MatchesTimeout } from "PFApp/activities/show/matches/matches_timeout";
import ActivityFilters from "PFApp/activities/show/parts/activity_filters";
import ActivityKeywords from "PFApp/activities/show/parts/activity_keywords";
import RefreshAction from "PFApp/activities/show/parts/refresh_action";
import { SideDetails } from "PFApp/activities/show/parts/side_details";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import SortBy from "PFApp/components/sort_by";
import { filterElements, letFiltersRestore } from "PFApp/use_filtered_collection";
import useIsChatEnabled from "PFApp/use_is_chat_enabled";
import BrainLoading from "PFComponents/brain_loading/brain_loading";
import Hr from "PFComponents/hr";
import { LoadingDots } from "PFComponents/loading_dots";
import PageLayout from "PFComponents/page/page_layout";
import PageTitle from "PFComponents/page/page_title";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import { getVisibleProperties } from "PFCore/helpers/templates";
import { useShortlistsInvalidate } from "PFCore/hooks/queries/shortlists/use_shortlists_invalidate";
import { Activity } from "PFTypes";
import PropTypes from "prop-types";
import React, { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./activity_matches.module.scss";
import MatchesItem from "./parts/matches_item";

export const ActivityMatches = ({ handleTitleClick, checkActivityPending, isEngagement, isRole }) => {
  const { task, matches, matchesFetchState, matchesMeta, updateMatchesParams, matchesParams, matchesReload } =
    useActivityPageContext();

  const { invalidateShortlists } = useShortlistsInvalidate();

  const { t } = useTranslation("activities");

  const { id, coowned, state, subtemplate_key } = task || {};
  const template = useActivityTemplate();
  const isLoading = !matchesFetchState || matchesFetchState === "pending";
  const isTimeout = matchesFetchState === "timeout";
  const isDraft = state === "draft";
  const showLoadingDots = isLoading && !matches;
  const fade = matches && matches.length && isLoading;

  const isChatEnabled = useIsChatEnabled();

  const isCompleted = state === "complete";

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [typesOpened, setTypesOpened] = useState<string[]>([]);
  const [filtersAccordionActiveIndex, setFiltersAccordionActiveIndex] = useState<number | null>(null);
  const { selectedFilters, areFiltersClear, updateFilter, resetFilters } = useActivityMatchesFilters();

  const templateProperties = useMemo(() => getVisibleProperties(template), [template]);

  const subtemplate = template?.schema?.subtemplates && template?.schema?.subtemplates[subtemplate_key!];
  const hasAvailability = !!templateProperties.find(({ name }) => name === "availability");
  const subtemplateProperties = useMemo(
    () =>
      subtemplate
        ? [...Object.keys(subtemplate.properties), ...(hasAvailability ? ["availability"] : [])]
        : [],
    [hasAvailability, subtemplate]
  );

  const schemaPropertiesNames = useMemo(() => {
    if (subtemplate) {
      return (
        templateProperties
          // If the prop is not in the subtemplate it is NOT activity filter
          .filter(({ name }) => subtemplateProperties.includes(name))
          // If the prop is marked as additional filter in the subtemplate is NOT an activity filter
          .filter(({ name, additional_filter }) => {
            const subtemplateProperty = subtemplate.properties[name];
            return !isNil(subtemplateProperty?.additional_filter)
              ? !subtemplateProperty.additional_filter
              : !additional_filter;
          })
          .map(({ name }) => name)
      );
    } else {
      return templateProperties.filter(({ additional_filter }) => !additional_filter).map(({ name }) => name);
    }
  }, [subtemplate, subtemplateProperties, templateProperties]);

  const handleOrderChange = (value) => {
    updateMatchesParams({ order: value, page: 1 });
  };

  const handleChangeTypeOpen = (open: boolean, typeName: string) => {
    setTypesOpened((typesOpened) => {
      if (open) {
        return [...typesOpened, typeName];
      } else {
        return typesOpened.filter((name) => name !== typeName);
      }
    });
  };

  const handleFiltersOpenChange = (open: boolean) => {
    setFiltersOpen(open);
    if (!open) {
      setFiltersAccordionActiveIndex(null);
      setTypesOpened([]);
    }
  };

  const letRestoreAll = letFiltersRestore(selectedFilters, matchesMeta.default_filters);
  const filtersCounter = isEmpty(matchesMeta.filters)
    ? 0
    : filterElements(matchesMeta.filters, schemaPropertiesNames).length;

  const orderMeta = matchesMeta?.order;
  const canShowFilters = matchesMeta?.filters;

  const matchingStateText: string | ReactNode = useMatchesStatusText(matchesMeta.status);

  const MATCHES_INFO = useMemo(
    () => ({
      brainLoading: <BrainLoading text={matchingStateText} />,
      matchesTimeout: <MatchesTimeout pending={checkActivityPending()} template={template || {}} />,

      matchesEmpty: (
        <MatchesEmpty
          task={task as Activity}
          matchesMeta={matchesMeta}
          openFilters={() => handleFiltersOpenChange(true)}
        />
      )
    }),
    [matchingStateText, checkActivityPending, template, task, matchesMeta]
  );

  const matchesInfoKey = useMemo<keyof typeof MATCHES_INFO>(() => {
    if (matchesMeta.status !== "ready") {
      return "brainLoading";
    }
    if (isLoading && isTimeout) {
      return "matchesTimeout";
    }
    return "matchesEmpty";
  }, [matchesMeta.status, isLoading, isTimeout]);

  if (!task) {
    return null;
  }
  return (
    <PageLayout mode="withSidebar">
      <div style={{ opacity: fade ? 0.5 : 1, transition: "opacity ease 300ms" }}>
        <PageTitle
          actions={
            <div className={css.actions}>
              {!isCompleted && coowned && (
                <RefreshAction
                  activityId={id}
                  onUpdate={() =>
                    matchesReload &&
                    matchesReload().then(() => {
                      invalidateShortlists(id);
                    })
                  }
                />
              )}
              {!isCompleted && !!orderMeta && (
                <SortBy
                  sort={orderMeta.value?.text}
                  options={orderMeta.options.map((option) => ({
                    id: option.text,
                    displayElement: option.text,
                    item: option
                  }))}
                  onSortChange={handleOrderChange}
                />
              )}
              {!isCompleted && canShowFilters && (
                <div style={{ marginLeft: 10 }}>
                  <FiltersButton
                    filtersAreClear={areFiltersClear}
                    onClick={() => handleFiltersOpenChange(!filtersOpen)}
                  />
                </div>
              )}
            </div>
          }
        >
          <div style={{ minWidth: 130 }}>{t("show.matches.header")}</div>
        </PageTitle>
        {filtersOpen && canShowFilters && (
          <div style={{ position: "relative", height: 2, zIndex: 120, marginTop: -20 }}>
            <FiltersBox
              narrow
              isLoading={matchesFetchState === "pending"}
              closeFilters={() => handleFiltersOpenChange(false)}
              filtersAreClear={areFiltersClear}
              meta={matchesMeta}
              clearFilters={resetFilters}
              accordionProps={{
                childrenTitle: t("show.activityFilters"),
                filtersTitle: filtersCounter
                  ? `${t("show.moreFilters")} (${filtersCounter})`
                  : t("show.moreFilters"),
                filtersDisabled: filtersCounter === 0,
                activeState: !isNil(filtersAccordionActiveIndex)
                  ? { [filtersAccordionActiveIndex]: true }
                  : {},
                onActiveIndexChange: (index) => setFiltersAccordionActiveIndex(index)
              }}
              handleFilterChange={updateFilter}
              blockedList={schemaPropertiesNames}
              style={{ marginBottom: 20, position: "absolute", width: "100%" }}
              letRestoreAll={letRestoreAll}
              restoreAllTooltipContent={t("show.matches.restoreAllTooltip")}
            >
              <div>
                <ActivityKeywords typesOpened={typesOpened} onChangeTypeOpen={handleChangeTypeOpen} />
                <ActivityFilters
                  meta={matchesMeta}
                  schemaPropertiesNames={schemaPropertiesNames}
                  handleFilterChange={updateFilter}
                />
              </div>
            </FiltersBox>
          </div>
        )}
        {showLoadingDots ? (
          <LoadingDots circlesEnabled pageViewEnabled />
        ) : (
          <div style={{ marginTop: 20 }}>
            {matches?.map((match) => (
              <MatchesItem key={match.id} match={match} withChat={isChatEnabled} />
            ))}
            {matches && matches.length > 0 ? (
              <SimplePagination
                currentPage={matchesParams?.page || 1}
                handleClick={(num) => {
                  updateMatchesParams && updateMatchesParams({ page: num });
                }}
                totalPages={matchesMeta.totalPages}
              />
            ) : (
              MATCHES_INFO[matchesInfoKey]
            )}
            <PageLayout.WhenOneColumn>
              <Hr />
              <SideDetails
                handleTitleClick={handleTitleClick}
                isEngagement={isEngagement}
                tab={"matches"}
                isRole={isRole}
                isDraft={isDraft}
              />
            </PageLayout.WhenOneColumn>
          </div>
        )}
      </div>
      <div>
        <PageLayout.WhenTwoColumns>
          <SideDetails
            handleTitleClick={handleTitleClick}
            isEngagement={isEngagement}
            tab={"matches"}
            isRole={isRole}
            isDraft={isDraft}
          />
        </PageLayout.WhenTwoColumns>
      </div>
    </PageLayout>
  );
};

ActivityMatches.propTypes = {
  handleTitleClick: PropTypes.func,
  checkActivityPending: PropTypes.func,
  isEngagement: PropTypes.bool.isRequired,
  isRole: PropTypes.bool.isRequired
};
